import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bike, Printer, Loader2 } from "lucide-react";

const BACKEND_ORIGIN = API.replace(/\/api$/, "");
const abs = (u) => (u ? `${BACKEND_ORIGIN}${u}` : "");

export default function PrintQRSheet() {
  const { pathId } = useParams();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const layout = sp.get("layout") === "4up" ? "4up" : "2up";
  const setLayout = (v) => setSp((prev) => { const n = new URLSearchParams(prev); n.set("layout", v); return n; }, { replace: true });

  useEffect(() => {
    api.get("/admin/rider-preview/print-sheet", { params: { bikePathId: pathId } })
      .then((r) => setData(r.data))
      .catch((e) => setErr(apiError(e.response?.data?.detail)));
  }, [pathId]);

  const today = new Date().toLocaleDateString();

  if (err) return <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="print-sheet-error">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const fourUp = layout === "4up";
  const gridCls = fourUp ? "grid grid-cols-2 gap-4" : "grid grid-cols-1 gap-6";
  const qrCls = fourUp ? "w-28 h-28" : "w-44 h-44";
  const cardCls = fourUp ? "min-h-[2.6in]" : "min-h-[4.6in]";

  return (
    <div className="min-h-screen bg-white text-slate-900 p-8" data-testid="print-qr-sheet">
      <style>{`
        @page { size: letter; margin: 0.5in; }
        @media print { .no-print { display:none !important; } .qr-card { break-inside: avoid; page-break-inside: avoid; } }
      `}</style>
      <div className="flex items-center justify-between mb-6 no-print">
        <div className="flex items-center gap-2"><Bike className="w-6 h-6 text-emerald-600" /><span className="font-bold text-lg">Velopaths</span><span className="text-slate-500">· Rider Preview QR Sheet</span></div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="layout-toggle">
            <button onClick={() => setLayout("2up")} className={`px-3 py-1.5 text-sm font-medium ${!fourUp ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`} data-testid="layout-2up">2-Up (Large)</button>
            <button onClick={() => setLayout("4up")} className={`px-3 py-1.5 text-sm font-medium ${fourUp ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`} data-testid="layout-4up">4-Up (Compact)</button>
          </div>
          <Button onClick={() => window.print()} data-testid="print-sheet-btn"><Printer className="w-4 h-4 mr-1" />Print / Save as PDF</Button>
        </div>
      </div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{data.bikePathName}</h1>
        <p className="text-slate-500 text-sm">Rider Preview QR codes · {layout === "4up" ? "4 per page" : "2 per page"} · Generated {today}</p>
      </div>
      {data.items.length === 0 ? (
        <div className="text-slate-500" data-testid="print-sheet-empty">No shops with an assigned zone in this bike path yet.</div>
      ) : (
        <div className={gridCls} data-testid="print-grid">
          {data.items.map((q) => (
            <div key={q.shopId} className={`qr-card border border-slate-200 rounded-xl p-5 flex gap-4 items-center ${cardCls}`} data-testid={`print-qr-${q.shopId}`}>
              <div className="bg-white border border-slate-100 rounded-lg p-2 shrink-0">
                <img src={abs(q.qrImageUrl)} alt="QR" className={qrCls} />
              </div>
              <div className="min-w-0">
                <div className={`font-bold truncate ${fourUp ? "text-base" : "text-xl"}`}>{q.shopName}</div>
                <div className="text-slate-500 text-sm">{q.bikePathName}</div>
                <div className="text-emerald-600 font-semibold text-sm mt-2">Scan to Preview Rider Map</div>
                <div className="text-[10px] text-slate-400 break-all mt-1">{q.previewUrl}</div>
                <div className="text-[10px] text-slate-400 mt-1">Generated {today}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-8 pt-4 border-t border-slate-200 text-center text-xs text-slate-400">
        Velopaths, LLC · Partner Program · Generated {today}
      </div>
    </div>
  );
}
